import React, { useEffect, useState } from 'react';
import styles from './DeleteUserModal.module.scss';
import { Button, LoaderOn, Modal, InputPartial, openNotification } from 'tt-ui-lib/core';
import { ReloadIcon } from 'tt-ui-lib/icons';
import { confirmUserDelete, requestUserDelete } from '../../../api/user/endpoints/profile';
import Cookies from 'js-cookie';

const DeleteUserModal = ({ open, setOpen }) => {
  const [confirmCode, setConfirmCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const initialTime = 300;
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  const storedStartTime = localStorage.getItem('startTime');
  const startTime = storedStartTime ? parseInt(storedStartTime, 10) : null;

  useEffect(() => {
    if (startTime) {
      setTimerActive(true);

      const interval = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        const remainingTime = initialTime - elapsed;

        if (remainingTime <= 0) {
          clearInterval(interval);
          setTimerActive(false);
          setElapsedTime(0);
          localStorage.removeItem('startTime');
        } else {
          setElapsedTime(remainingTime);
        }

        localStorage.setItem('startTime', startTime.toString());
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [startTime]);

  const startTimer = () => {
    if (!timerActive) {
      const now = Date.now();
      localStorage.setItem('startTime', now.toString());
      setElapsedTime(initialTime);
      setTimerActive(true);
    }
  };

  const sendDeleteRequest = (withLoading = false) => {
    if (withLoading) setIsLoading(true);

    requestUserDelete()
      .then(() => {
        startTimer();
      })
      .catch((e) => {
        if (e.response?.status === 400) {
          openNotification({
            type: 'info',
            message: 'Try to resend confirmation code after 5 minutes',
          });
          return;
        }

        openNotification({
          type: 'error',
          message: 'Error while sending delete confirmation code, try again later',
        });
      })
      .finally(() => {
        if (withLoading) setIsLoading(false);
      });
  };

  const confirmDelete = () => {
    setIsLoading(true);

    confirmUserDelete(confirmCode)
      .then(() => {
        openNotification({
          type: 'success',
          message: 'Your user profile was successfully deleted!',
        });

        setTimeout(() => {
          const domainValue = process.env.REACT_APP_DOMAIN;
          Cookies.remove('access_token', { domain: domainValue });
          Cookies.remove('refresh_token', { domain: domainValue });
          Cookies.remove('userId', { domain: domainValue });
          Cookies.remove('companyId', { domain: domainValue });
          Cookies.remove('calculatorId', { domain: domainValue });
          localStorage.removeItem('currentUserData');
          localStorage.removeItem('currentCompanyData');

          window.location.href = '/join';
        }, 2000);
      })
      .catch(() => {
        openNotification({
          type: 'error',
          message: 'Error while deleting profile, try again later',
        });
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      sendDeleteRequest(true);
    }
  }, [open]);

  const resendCode = () => {
    sendDeleteRequest();
  };

  const closeModal = () => {
    setOpen(false);
  };

  const displayCountdown = () => {
    const minutes = Math.floor(elapsedTime / 60);
    const seconds = elapsedTime % 60;

    return `Resend code again in ${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  return (
    <Modal
      title={`Do you really want to delete profile? `}
      open={open}
      onClose={closeModal}
      className={styles.body}
      closeOnlyByControls
    >
      {isLoading && <LoaderOn />}

      <div className={styles.main}>
        <div className={styles.text}>
          We are sorry to see you go! <br />
          All your data will be removed from TransparenTerra. If you are sure you want to delete
          your account, please enter the confirmation code sent to your email address
        </div>

        <div className={styles.inputWrapper}>
          <InputPartial
            value={confirmCode}
            onChange={(value) => setConfirmCode(value)}
            length={8}
          />

          <div className={styles.refreshBlock}>
            {elapsedTime > 0 && <div>{displayCountdown()}</div>}
            <Button
              className={styles.refreshBtn}
              type="link"
              disabled={elapsedTime > 0}
              onClick={() => resendCode()}
            >
              <ReloadIcon /> <p>Resend code</p>
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.buttons}>
        <Button onClick={closeModal}>Cancel</Button>
        <Button type="primary" disabled={!confirmCode} onClick={confirmDelete}>
          Delete
        </Button>
      </div>
    </Modal>
  );
};

export default DeleteUserModal;
